import { Add } from '@mui/icons-material';
import { Box, Fab, Grid, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { formatDate } from '../components/formatUtils/dateFormatUtils';
import { useNavigateDialogForm } from '../components/formikDialogForm/dialogForm.hook';
import { AppDataGrid } from '../components/table/AppDataGrid';
import { Aufwand } from '../features/aufwand/api/types';
import { selectMitarbeiterId } from '../features/auth/api/authSlice';
import { KundeDisplay } from '../features/kunde/components/KundeDisplay';
import { useFindAllOwnedProjektQuery } from '../features/projekt/api/projektApi';
import { ExternalProjektDetails } from '../features/projekt/view/details/ExternalProjektDetails';
import { useAppSelector } from '../state/redux.hooks';

const projektTableColumns: GridColDef[] = [
  {
    field: 'name',
    headerName: 'Name',
    flex: 3,
  },
  {
    field: 'kurzbeschreibung',
    headerName: 'Kurzbeschreibung',
    flex: 4,
    type: 'string',
  },
  {
    field: 'kundeId',
    headerName: 'Kunde',
    flex: 2,
    renderCell: params => <KundeDisplay id={params.value} />,
  },
  {
    field: 'creationDate',
    headerName: 'Angelegt am',
    flex: 1,
    type: 'date',
    valueFormatter: value => formatDate(value as string),
  },
];

export const getExternProjektPath = (projektId: string | number): string => `/extern/${projektId}`;

export const getSelectedExternProjektId = (splatParam?: string): string | undefined =>
  splatParam?.split('/').filter(Boolean).at(-1);

export const ExternAppProjekte: FC = () => {
  const { data: projekte } = useFindAllOwnedProjektQuery();

  const navigate = useNavigate();
  const params = useParams();

  const mitarbeiterId = useAppSelector(selectMitarbeiterId) ?? undefined;

  const selectedProjektId = getSelectedExternProjektId(params['*']);

  const currentDate = new Date();

  const navigateToCreateAufwand = useNavigateDialogForm<Aufwand>('aufwand', 'create', {
    projektId: selectedProjektId,
    mitarbeiterId,
    datum: currentDate.toISOString(),
  });

  return (
    <Box
      component="main"
      sx={{
        px: 2,
        pt: 3,
        height: '90vh',
        position: 'relative',
      }}
    >
      <Grid
        container
        spacing={3}
        sx={{
          height: '100%',
        }}
      >
        <Grid
          size={{ md: 6, xs: 12 }}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant={'h5'}
            sx={{
              m: 1,
              color: 'text.secondary',
            }}
          >
            Meine Projekte
          </Typography>
          <AppDataGrid
            style={{ flexGrow: 1 }}
            hideFooter
            disableMultipleRowSelection
            columns={projektTableColumns}
            rows={projekte?.data ?? []}
            onRowClick={({ id }) => navigate(getExternProjektPath(id))}
          />
        </Grid>
        <Grid
          size={{ md: 6, xs: 12 }}
          sx={{
            height: '100%',
          }}
        >
          {selectedProjektId ? (
            <ExternalProjektDetails id={selectedProjektId} />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '30%',
              }}
            >
              <Typography variant="h4" sx={{ textAlign: 'center', mb: 2 }}>
                Willkommen
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  textAlign: 'center',
                  mb: 2,
                }}
              >
                Wähle ein Projekt aus der Liste und erstelle mit dem Plus, unten rechts, Aufwände.
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  textAlign: 'center',
                }}
              >
                Viel Spaß!
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
      <Fab
        color="primary"
        disabled={!selectedProjektId}
        sx={{ position: 'absolute', bottom: -20, right: 8 }}
        onClick={() => navigateToCreateAufwand()}
      >
        <Add />
      </Fab>
    </Box>
  );
};
